import { tileTexture } from '../animation/Theme';
import { normalizePlayerNames, type RunSettings } from '../multiplayer/RunSettings';
import { generateSurpriseTestStage, createLabPanel } from '../levels/SurpriseTestStage';
import { POWERUP_LEGEND } from '../ui/PowerUpLegend';
import Phaser from 'phaser';
import {
  TILE, MAP_COLS, MAP_ROWS, MAP_W, MAP_H, GAME_W, GAME_H, HUD_HEIGHT,
  BOMB_EXPLODE_MS, LEVEL_COMPLETE_DELAY_MS, RESPAWN_DELAY_MS,
  WORLD_COLORS, PLAYER_COLORS,
} from '../constants';
import { CellType, BombState, GameMode, EnemyType, PowerUpType, type Grid, type ExplosionCell, type LevelConfig } from '../types';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { Bomb } from '../entities/Bomb';
import { PowerUp } from '../entities/PowerUp';
import { BombSystem } from '../systems/BombSystem';
import { ExplosionSystem } from '../systems/ExplosionSystem';
import { ScoreSystem } from '../systems/ScoreSystem';
import { HUD } from '../ui/HUD';
import { MobileControls } from '../ui/MobileControls';
import { audioSystem } from '../systems/AudioSystem';
import { saveSystem } from '../systems/SaveSystem';
import { events } from '../EventBus';
import { getLevelConfig } from '../levels/LevelDefinitions';
import { generateLevel, type GeneratedLevel } from '../levels/LevelGenerator';

interface SceneData extends RunSettings {
  level: number;
  mode: GameMode;
  score: number;
  playerCount?: number;
}

// Tracks active explosion cells for damage detection
interface ActiveExplosion {
  cells: ExplosionCell[];
  expiry: number;
}

export class GameScene extends Phaser.Scene {
  // ── Config ──────────────────────────────────────────────────────────────────
  private currentLevel = 1;
  private currentMode: GameMode = GameMode.CAMPAIGN;
  private currentScore = 0;
  private playerCount = 1;
  private playerNames: string[] = [];
  private testStage = false;
  private labStatus: HTMLParagraphElement | null = null;
  private roster: Phaser.GameObjects.Text[] = [];
  private levelConfig!: LevelConfig;
  private generated!: GeneratedLevel;

  // ── State ───────────────────────────────────────────────────────────────────
  private grid!: Grid;
  private timeLeft = 180;
  private levelComplete = false;
  private gameOverFlag = false;
  private transitionActive = false;
  private exitActive = false;
  private paused = false;
  private survivalWave = 1;
  private survivalElapsed = 0;
  private waveBanner: Phaser.GameObjects.Text | null = null;
  private nextWavePending = false;

  // ── Tile visuals ─────────────────────────────────────────────────────────────
  private bgRT!: Phaser.GameObjects.RenderTexture;
  private blockMap = new Map<string, Phaser.GameObjects.Image>();
  private exitSprite: Phaser.GameObjects.Graphics | null = null;
  private exitCol = -1;
  private exitRow = -1;

  // ── Snow particles ────────────────────────────────────────────────────────────
  private snowGfx!: Phaser.GameObjects.Graphics;
  private snowflakes: Array<{x: number; y: number; speed: number; size: number; drift: number; phase: number}> = [];

  // ── Entities ─────────────────────────────────────────────────────────────────
  private players: Player[] = [];
  private enemies: Enemy[] = [];
  private bombs = new Map<number, Bomb>();
  private powerups: PowerUp[] = [];

  // ── Systems ──────────────────────────────────────────────────────────────────
  private bombSystem!: BombSystem;
  private explosionSystem!: ExplosionSystem;
  private scoreSystem!: ScoreSystem;
  private hud!: HUD;
  private mobileControls!: MobileControls;

  // ── Explosion tracking (for damage) ─────────────────────────────────────────
  private activeExplosions: ActiveExplosion[] = [];

  // ── Input ────────────────────────────────────────────────────────────────────
  private escKey!: Phaser.Input.Keyboard.Key;
  private mKey!: Phaser.Input.Keyboard.Key;

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data: SceneData): void {
    this.currentLevel = data.level ?? 1;
    this.currentMode  = data.mode ?? GameMode.CAMPAIGN;
    this.currentScore = data.score ?? 0;
    this.testStage = Boolean(data.testStage);
    this.playerCount = this.currentMode === GameMode.MULTIPLAYER && !this.testStage ? Math.min(4, Math.max(2, data.playerCount ?? 2)) : 1;
    this.playerNames = normalizePlayerNames(this.playerCount, data.playerNames);
    this.roster = []; this.labStatus = null;
    this.survivalWave = 1; this.survivalElapsed = 0; this.waveBanner = null; this.nextWavePending = false;

    // Reset per-level state
    this.levelComplete    = false;
    this.gameOverFlag     = false;
    this.transitionActive = false;
    this.exitActive       = false;
    this.exitSprite       = null;
    this.exitCol          = -1;
    this.exitRow          = -1;
    this.paused           = false;
    this.activeExplosions = [];
    this.blockMap.clear();
    this.bombs.clear();
  }

  create(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    // ── Systems ─────────────────────────────────────────────────────────────
    events.removeAll();

    this.bombSystem      = new BombSystem();
    this.explosionSystem = new ExplosionSystem(this);
    this.scoreSystem     = new ScoreSystem();
    this.scoreSystem.resetScore();
    // Re-add accumulated score
    if (this.currentScore > 0) this.scoreSystem.add(this.currentScore);
    this.scoreSystem.reset(this.currentLevel);

    // ── Level generation ─────────────────────────────────────────────────────
    this.levelConfig = getLevelConfig(this.currentLevel);
    if (!this.testStage && this.currentMode === GameMode.ARCADE) this.levelConfig = {
      ...getLevelConfig(3), level: 1, seed: Math.floor(Math.random()*0x7fffffff),
      enemyCount: 5, timeLimit: 150, blockDensity: 0.35, bossLevel: false,
    };
    if (!this.testStage && this.currentMode === GameMode.SURVIVAL) this.levelConfig = {
      ...getLevelConfig(2), seed: Math.floor(Math.random()*0x7fffffff), enemyCount: 3, blockDensity: 0.25,
    };
    this.generated = this.testStage ? generateSurpriseTestStage() : generateLevel(this.levelConfig);
    this.grid        = this.generated.grid;
    this.bombSystem.grid = this.grid;

    const world = Math.ceil(this.currentLevel / 6);
    const theme = WORLD_COLORS[Math.min(world - 1, WORLD_COLORS.length - 1)];

    // ── Camera ──────────────────────────────────────────────────────────────
    this.cameras.main.setBackgroundColor(theme.bg);
    this.cameras.main.setBounds(0, 0, GAME_W, GAME_H);

    // ── Render tiles ────────────────────────────────────────────────────────
    this.bgRT = this.add.renderTexture(0, 0, MAP_W, MAP_H).setDepth(0).setOrigin(0, 0);
    this.renderMap(theme);
    this.addTropicalDecorations();

    // ── Spawn entities ──────────────────────────────────────────────────────
    this.spawnPlayers();
    this.spawnEnemies();
    this.spawnPowerups();

    // ── HUD ─────────────────────────────────────────────────────────────────
    this.hud = new HUD(this);
    this.hud.setPracticeMode(this.testStage);
    this.hud.setElapsedMode(this.currentMode === GameMode.SURVIVAL);
    this.timeLeft = this.testStage ? 999 : this.levelConfig.timeLimit;
    if (this.testStage) this.setupTestStage();
    if (this.currentMode === GameMode.MULTIPLAYER) this.setupRoster();
    if (this.currentMode === GameMode.SURVIVAL) this.waveBanner = this.add.text(20, 8, 'SUPRAVIEȚUIRE • VAL 1',
      { fontFamily: 'Arial', fontSize: '18px', color: '#ddf3c0', backgroundColor: '#102033', padding: {x:8,y:5} }).setDepth(30);

    // ── Snow particles ───────────────────────────────────────────────────────
    this.snowGfx = this.add.graphics().setDepth(50).setScrollFactor(0);
    this.snowflakes = saveSystem.settings.theme === 'penguin' ? Array.from({length:22},()=>({x:Phaser.Math.Between(0,MAP_W),y:Phaser.Math.Between(0,MAP_H),speed:Phaser.Math.Between(12,28),size:Math.random()*1.3+0.6,drift:5,phase:Math.random()*Math.PI*2})) : [];
    // ── Mobile controls ─────────────────────────────────────────────────────
    this.mobileControls = new MobileControls(this);
    this.events.on('mobile:pause', this.showPause, this);

    // ── Input ────────────────────────────────────────────────────────────────
    const kb = this.input.keyboard!;
    this.escKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    this.mKey   = kb.addKey(Phaser.Input.Keyboard.KeyCodes.M);

    // ── Event listeners ──────────────────────────────────────────────────────
    this.bindEvents();

    // ── Audio ────────────────────────────────────────────────────────────────
    if (this.levelConfig.bossLevel) {
      audioSystem.startMusic('boss');
    } else {
      audioSystem.startMusic('game');
    }

    // Unlock audio on first interaction
    this.input.once('pointerdown', () => audioSystem.resume());
  }

  update(_time: number, delta: number): void {
    if (this.transitionActive || this.paused) return;

    // ── Timer ────────────────────────────────────────────────────────────────
    if (this.currentMode === GameMode.SURVIVAL) this.survivalElapsed += delta / 1000;
    if (!this.testStage && this.currentMode !== GameMode.SURVIVAL && !this.levelComplete && !this.gameOverFlag) {
      this.timeLeft -= delta / 1000;
      if (this.timeLeft <= 0) {
        this.timeLeft = 0;
        this.onTimeUp();
      }
    }

    if (this.levelComplete || this.gameOverFlag) return;

    // ── Players ──────────────────────────────────────────────────────────────
    for (const p of this.players) {
      if (!p.active) continue;
      p.update(delta, p.playerId === 0 ? this.mobileControls.input : undefined);
    }

    this.roster.forEach((label, i) => label.setText(`${this.playerNames[i]} • ${this.players[i].stats.lives} vieți`)
      .setAlpha(this.players[i].isDead ? 0.4 : 1));
    if (this.labStatus) {
      const s = this.players[0].stats;
      this.labStatus.textContent = `Viteză ×${s.speedMult} • Bombe ${s.maxBombs} • Rază ${s.blastRadius} • Vieți ${s.lives} • Scut ${s.hasShield ? 'DA' : 'NU'} • Foc ${Math.ceil(s.fireImmunityTimer / 1000)}s • Trecere lăzi ${s.hasWallPass ? 'DA' : 'NU'} • Trecere bombe ${s.hasBombPass ? 'DA' : 'NU'} • Împingere ${s.hasBombKick ? 'DA' : 'NU'} • Telecomandă ${s.hasRemoteBomb ? 'DA' : 'NU'}`;
    }
    // ── Enemies ──────────────────────────────────────────────────────────────
    const dangerCells = this.bombSystem.getDangerCells();
    const p0 = this.players[0];
    const p0Pos = p0?.active ? p0.getGridPos() : { col: 7, row: 6 };

    for (const e of this.enemies) {
      if (!e.alive || !e.active) continue;
      e.updateDangerMap(dangerCells);
      e.updatePlayerPos(p0Pos.col, p0Pos.row);
      e.update(delta, this.grid);

      // Bomber AI: place bomb
      if (e.type === EnemyType.BOMBER && e.canPlaceBomb()) {
        this.doPlaceBomb(-1, e.col, e.row, 2, false, false);
        e.resetBombTimer();
      }
    }

    // ── Bombs ────────────────────────────────────────────────────────────────
    const toExplode = this.bombSystem.update(delta);
    for (const bombData of toExplode) {
      this.triggerExplosion(bombData.id);
    }

    // Logical bombs own timers and positions; visuals follow the same source of truth.
    for (const data of this.bombSystem.getAllBombs()) this.bombs.get(data.id)?.sync(data);
    // Update visual bombs
    for (const [id, bombVisual] of this.bombs) {
      if (!bombVisual.active) { this.bombs.delete(id); continue; }
      bombVisual.update(delta);
      if (bombVisual.shouldExplode) {
        this.triggerExplosion(id);
      }
    }

    // ── Collisions ───────────────────────────────────────────────────────────
    this.checkExplosionCollisions();
    this.checkEnemyCollisions();
    this.checkPowerUpCollisions();
    this.checkExitCollision();

    // ── Snow particles ───────────────────────────────────────────────────────
    this.updateSnow(delta);

    // ── HUD update ───────────────────────────────────────────────────────────
    const pl = this.players[0];
    if (pl) { this.mobileControls.setRemoteAvailable(pl.stats.hasRemoteBomb); this.mobileControls.updateStatus(pl.stats.lives, pl.stats.maxBombs, pl.stats.speedMult); }
    if (pl) {
      this.hud.update({
        score: this.scoreSystem.score,
        lives: pl.stats.lives,
        timeLeft: this.currentMode === GameMode.SURVIVAL ? Math.floor(this.survivalElapsed) : Math.ceil(this.timeLeft),
        level: this.currentLevel,
        world: Math.ceil(this.currentLevel / 6),
        maxBombs: pl.stats.maxBombs,
        activeBombs: pl.stats.maxBombs - (this.bombSystem.getBombsByOwner(0).filter(b => b.state === BombState.ARMED).length),
        blastRadius: pl.stats.blastRadius,
        speedMult: pl.stats.speedMult,
        powerups: Array.from(pl.stats.activePowerUps),
      });
    }

    // ── ESC: pause ───────────────────────────────────────────────────────────
    if (Phaser.Input.Keyboard.JustDown(this.escKey)) {
      this.showPause();
    }

    if (Phaser.Input.Keyboard.JustDown(this.mKey)) {
      audioSystem.toggleMute();
    }

    // ── Level complete check ─────────────────────────────────────────────────
    this.checkLevelComplete();
  }

  // ── Tile rendering ─────────────────────────────────────────────────────────

  private renderMap(_theme: typeof WORLD_COLORS[0]): void {
    const worldIdx = Math.min(Math.ceil(this.currentLevel / 6) - 1, WORLD_COLORS.length - 1);
    const T = TILE;

    for (let row = 0; row < MAP_ROWS; row++) {
      for (let col = 0; col < MAP_COLS; col++) {
        const cx = col * T + T / 2;
        const cy = row * T + T / 2;
        const cell = this.grid[row][col];
        const floorKey = (row + col) % 2 === 0
          ? tileTexture('floor_a', worldIdx)
          : tileTexture('floor_b', worldIdx);

        switch (cell) {
          case CellType.FLOOR:
            this.bgRT.stamp(floorKey, 0, cx, cy);
            break;
          case CellType.WALL:
            this.bgRT.stamp(tileTexture('wall', worldIdx), 0, cx, cy);
            break;
          case CellType.BLOCK:
            this.bgRT.stamp(floorKey, 0, cx, cy);
            this.createBlockSprite(col, row, worldIdx);
            break;
          case CellType.EXIT:
            this.bgRT.stamp(floorKey, 0, cx, cy);
            break;
        }
      }
    }
  }

  private addTropicalDecorations(): void {
    if (saveSystem.settings.theme === 'penguin') {
      const props: [string,number,number,number][] = [
        ['tree',0,0,0.46],['lamp',MAP_COLS-1,0,0.45],
        ['rocks',0,MAP_ROWS-1,0.4],['gift',MAP_COLS-1,MAP_ROWS-1,0.43],
        ['snow',4,0,0.4],['snow',10,0,0.4],['fence',7,MAP_ROWS-1,0.43],
      ];
      for(const [name,col,row,scale] of props)this.add.image(col*TILE+TILE/2,row*TILE+TILE/2,'winter',`decorations/${name}`).setScale(scale).setDepth(2);
      return;
    }
    if (saveSystem.settings.theme === 'walker') {
      for(const [name,col,row,scale] of [['palm',0,0,0.45],['barrel',MAP_COLS-1,0,0.43],['flowers',0,MAP_ROWS-1,0.43],['flowers',MAP_COLS-1,MAP_ROWS-1,0.43]] as const)this.add.image(col*TILE+TILE/2,row*TILE+TILE/2,'walker',`decorations/${name}`).setScale(scale).setDepth(2);
      return;
    }
    // Decorations stay on already blocked border cells; their artwork never changes hitboxes.
    const props: [string, number, number, number][] = [
      ['palm', 0, 0, 0.55], ['umbrella', MAP_COLS-1, 0, 0.5],
      ['barrel', 0, MAP_ROWS-1, 0.4], ['rocks', MAP_COLS-1, MAP_ROWS-1, 0.45],
      ['flowers', 4, 0, 0.4], ['plant', 10, 0, 0.4], ['sign', 7, MAP_ROWS-1, 0.4],
    ];
    for (const [name,col,row,scale] of props) this.add.image(col*TILE+TILE/2,row*TILE+TILE/2,'summer',`decorations/${name}`).setScale(scale).setDepth(2);
    for (const col of [2,3,11,12]) this.add.image(col*TILE+TILE/2,TILE/2,'summer','tiles/water').setScale(0.48).setDepth(1);
  }

  private createBlockSprite(col: number, row: number, worldIdx: number): void {
    const cx = col * TILE + TILE / 2;
    const cy = row * TILE + TILE / 2;
    const img = this.add.image(cx, cy, tileTexture('block', worldIdx)).setDepth(1);
    this.blockMap.set(`${col},${row}`, img);
  }

  // ── Player management ───────────────────────────────────────────────────────

  private spawnPlayers(): void {
    const count = this.currentMode === GameMode.MULTIPLAYER ? (this.playerCount || 2) : 1;
    for (let i = 0; i < count; i++) {
      const start = this.generated.playerStarts[i] ?? this.generated.playerStarts[0];
      const p = new Player(this, start.col, start.row, i);
      p.setGrid(this.grid);
      p.setBombCollisionQuery((col, row) => Boolean(this.bombSystem.getBombAt(col, row)));
      p.onRemoteDetonate = () => {
        if (!this.bombSystem.getBombsByOwner(i).some(b => b.remote && b.state === BombState.ARMED)) return false;
        this.bombSystem.remoteDetonate(i); return true;
      };
      p.onKickBomb = (col, row, dx, dy) => {
        const bomb = this.bombSystem.getBombAt(col, row);
        if (!bomb) return false;
        // Do not push a bomb into an occupied character cell.
        const nextCol = col + dx, nextRow = row + dy;
        const occupied = [...this.players, ...this.enemies].some(actor => actor.active &&
          Math.hypot(actor.x - (nextCol * TILE + TILE / 2), actor.y - (nextRow * TILE + TILE / 2)) < TILE * 0.6);
        if (occupied || !this.bombSystem.kickBomb(bomb.id, dx, dy)) return false;
        for (const owner of this.players) owner.onBombMoved(col, row, nextCol, nextRow);
        this.bombs.get(bomb.id)?.sync(bomb); return true;
      };
      p.onPlaceBomb = (col, row) => {
        return this.doPlaceBomb(i, col, row, p.stats.blastRadius, p.stats.hasBombKick, p.stats.hasRemoteBomb);
      };
      if (this.currentMode === GameMode.MULTIPLAYER) {
        p.add(this.add.text(0, -56, this.playerNames[i], { fontFamily: 'Arial', fontSize: '13px',
          color: '#' + PLAYER_COLORS[i].toString(16).padStart(6, '0'), stroke: '#000000', strokeThickness: 3 }).setOrigin(0.5));
      }
      p.setDepth(5);
      this.players.push(p);
    }
  }

  // ── Enemy management ────────────────────────────────────────────────────────

  private spawnEnemies(): void {
    if (this.testStage) return;
    const cfg = this.levelConfig;
    const types = cfg.enemyTypes;
    const starts = this.generated.enemyStarts;

    for (let i = 0; i < Math.min(cfg.enemyCount, starts.length); i++) {
      const type = types[i % types.length];
      const s = starts[i];
      const e = new Enemy(this, s.col, s.row, type);
      e.setBombCollisionQuery((col, row) => {
        const bomb = this.bombSystem.getBombAt(col, row);
        return bomb?.state === BombState.ARMED || bomb?.state === BombState.PLACED;
      });
      e.speed *= 1.2;
      e.setDepth(4);
      this.enemies.push(e);
    }
  }

  // ── PowerUp management ──────────────────────────────────────────────────────

  private spawnPowerups(): void {
    for (const pu of this.generated.powerups) {
      const p = new PowerUp(this, pu.col, pu.row, pu.type);
      p.setDepth(2);
      this.powerups.push(p);
    }
    // Exit is hidden under a block initially
    this.exitCol = this.generated.exitCol;
    this.exitRow = this.generated.exitRow;
  }

  // ── Bomb logic ───────────────────────────────────────────────────────────────

  private doPlaceBomb(
    ownerId: number, col: number, row: number,
    radius: number, hasKick: boolean, remote: boolean
  ): boolean {
    // Check nothing already there
    if (this.bombSystem.getBombAt(col, row)) return false;

    const data = this.bombSystem.placeBomb(col, row, radius, ownerId, hasKick, remote);
    if (!data) return false;

    // Create visual
    const bombVisual = new Bomb(this, col, row, radius, ownerId, remote, hasKick);
    bombVisual.setDepth(3);
    this.bombs.set(data.id, bombVisual);

    // Notify player entity
    const player = this.players[ownerId];
    if (player) {
      // Already tracked via activeBombs in Player
    }

    audioSystem.play('bomb_place');
    if (!this.testStage) saveSystem.incrementStat('totalBombs');
    return true;
  }

  private triggerExplosion(bombId: number): void {
    const bombVisual = this.bombs.get(bombId);
    if (!bombVisual || bombVisual.bombState === BombState.EXPLODING || bombVisual.bombState === BombState.REMOVED) {
      return;
    }

    bombVisual.beginExploding();

    // Free the player's bomb slot immediately — must happen before any early return
    const ownerId = bombVisual.ownerId;
    const bCol = bombVisual.col;
    const bRow = bombVisual.row;
    this.players[ownerId]?.onBombExploded(bCol, bRow);

    const cells = this.bombSystem.explodeBomb(bombId);
    if (!cells) {
      bombVisual.markRemoved();
      this.time.delayedCall(100, () => { if (bombVisual.active) bombVisual.destroy(); });
      this.bombs.delete(bombId);
      return;
    }

    // Create explosion visuals
    this.explosionSystem.createExplosion(cells);

    // Record active explosion for damage detection
    const expiry = this.time.now + BOMB_EXPLODE_MS;
    this.activeExplosions.push({ cells, expiry });

    // Handle block destruction visuals
    for (const cell of cells) {
      const key = `${cell.col},${cell.row}`;
      const blockRect = this.blockMap.get(key);
      if (blockRect) {
        // Animate block destruction
        this.tweens.add({
          targets: blockRect,
          scaleX: 1.3,
          scaleY: 1.3,
          alpha: 0,
          duration: 250,
          onComplete: () => blockRect.destroy(),
        });
        this.blockMap.delete(key);
        // Remove block from background graphics
        this.redrawTileAsFloor(cell.col, cell.row);
        audioSystem.play('block_break');

        // Check if exit was hidden here
        if (this.currentMode !== GameMode.SURVIVAL && cell.col === this.exitCol && cell.row === this.exitRow) {
          this.revealExit();
        }

        // Check for powerup hidden here
        const pu = this.powerups.find(p => p.getGridPos().col === cell.col && p.getGridPos().row === cell.row);
        if (pu && !pu.collected) {
          pu.setVisible(true);
        }
      }
    }

    bombVisual.markRemoved();
    // Destroy visual after brief delay
    this.time.delayedCall(100, () => {
      if (bombVisual.active) bombVisual.destroy();
    });
    this.bombs.delete(bombId);

    audioSystem.play('bomb_explode');
  }

  private redrawTileAsFloor(col: number, row: number): void {
    const worldIdx = Math.min(Math.ceil(this.currentLevel / 6) - 1, WORLD_COLORS.length - 1);
    const T = TILE;
    const floorKey = (row + col) % 2 === 0
      ? tileTexture('floor_a', worldIdx)
      : tileTexture('floor_b', worldIdx);
    this.bgRT.stamp(floorKey, 0, col * T + T / 2, row * T + T / 2);
    this.grid[row][col] = CellType.FLOOR;
  }

  private revealExit(): void {
    if (this.exitSprite?.active) return;
    this.exitSprite = null;
    if (this.exitCol < 0 || this.exitRow < 0) {
      const start = this.generated.playerStarts[0];
      this.exitCol = start.col; this.exitRow = start.row;
    }
    const key = `${this.exitCol},${this.exitRow}`;
    this.blockMap.get(key)?.destroy();
    this.blockMap.delete(key);
    this.redrawTileAsFloor(this.exitCol, this.exitRow);
    const x = this.exitCol * TILE + TILE / 2;
    const y = this.exitRow * TILE + TILE / 2;
    this.grid[this.exitRow][this.exitCol] = CellType.EXIT;

    const g = this.add.graphics().setDepth(2);
    g.fillStyle(0x00ffff, 0.3);
    g.fillCircle(0, 0, TILE / 2 - 4);
    g.lineStyle(3, 0x00ffff, 0.9);
    g.strokeCircle(0, 0, TILE / 2 - 4);
    g.lineStyle(2, 0xffffff, 0.6);
    g.strokeCircle(0, 0, TILE / 2 - 10);
    g.setPosition(x, y);
    this.exitSprite = g;

    // Pulsing animation
    this.tweens.add({
      targets: g,
      scaleX: 1.15,
      scaleY: 1.15,
      alpha: 0.7,
      duration: 600,
      yoyo: true,
      repeat: -1,
    });
  }

  private activateExit(): void {
    if (this.exitActive || !this.exitSprite) return;
    this.exitActive = true;

    // Change exit appearance to indicate it's active
    this.exitSprite.clear();
    this.exitSprite.fillStyle(0x00ff44, 0.5);
    this.exitSprite.fillCircle(0, 0, TILE / 2 - 2);
    this.exitSprite.lineStyle(4, 0x00ff44, 1.0);
    this.exitSprite.strokeCircle(0, 0, TILE / 2 - 2);
    this.exitSprite.fillStyle(0xffffff, 0.8);
    this.exitSprite.fillCircle(0, 0, 8);

    events.emit('exit:opened', {});
  }

  // ── Collision detection ─────────────────────────────────────────────────────

  private checkExplosionCollisions(): void {
    const now = this.time.now;
    // Prune expired explosions
    this.activeExplosions = this.activeExplosions.filter(e => e.expiry > now);

    for (const explosion of this.activeExplosions) {
      for (const cell of explosion.cells) {
        const cellKey = `${cell.col},${cell.row}`;

        // Check players
        for (const player of this.players) {
          if (!player.active || player.isDead || player.isDying) continue;
          const pp = player.getGridPos();
          if (pp.col === cell.col && pp.row === cell.row) {
            const died = player.takeDamage();
            if (died) {
              this.onPlayerDied(player);
            } else {
              this.hud.flash(0xff4444);
              audioSystem.play('player_die');
            }
          }
        }

        // Check enemies
        for (let i = this.enemies.length - 1; i >= 0; i--) {
          const enemy = this.enemies[i];
          if (!enemy.alive || !enemy.active) continue;
          if (enemy.col === cell.col && enemy.row === cell.row) {
            const dead = enemy.takeDamage();
            if (dead) {
              this.onEnemyKilled(enemy, i);
            }
          }
        }

        void cellKey; // suppress unused warning
      }
    }
  }

  private checkEnemyCollisions(): void {
    // Use interpolated positions so touching an enemy between cells also counts.
    const touchDistance = TILE * 0.45;
    for (const player of this.players) {
      if (!player.active || player.isDead || player.isDying) continue;
      for (const enemy of this.enemies) {
        if (!enemy.active || !enemy.alive) continue;
        if (Phaser.Math.Distance.Between(player.x, player.y, enemy.pixelX, enemy.pixelY) < touchDistance) {
          if (player.takeDamage('enemy')) this.onPlayerDied(player);
          if (player.isDying) break;
        }
      }
    }
  }

  private checkPowerUpCollisions(): void {
    for (let i = this.powerups.length - 1; i >= 0; i--) {
      const pu = this.powerups[i];
      if (pu.collected || !pu.active || !pu.visible) continue;

      const puPos = pu.getGridPos();
      // A visible surprise on a crate is only collected after the crate is opened.
      if (this.grid[puPos.row][puPos.col] === CellType.BLOCK) continue;

      for (const player of this.players) {
        if (!player.active || player.isDead || player.isDying) continue;
        const pp = player.getGridPos();
        if (pp.col === puPos.col && pp.row === puPos.row) {
          player.addPowerUp(pu.type);
          const name = POWERUP_LEGEND.find(item => item.type === pu.type)?.name.split(' • ')[1] ?? 'Surpriză';
          const message = pu.type === PowerUpType.SPEED_UP
            ? `VITEZĂ ×${player.stats.speedMult.toFixed(1)}` : name.toUpperCase();
          const feedback = this.add.text(player.x, player.y - 38, message, {
            fontFamily: 'Arial Black, Arial', fontSize: '18px', color: '#99ff77',
            stroke: '#142218', strokeThickness: 4,
          }).setOrigin(0.5).setDepth(20);
          this.tweens.add({ targets: feedback, y: feedback.y - 28, alpha: 0,
            duration: 1500, onComplete: () => feedback.destroy() });
          pu.collect();
          events.emit('powerup:collected', { type: pu.type, playerId: player.playerId });
          audioSystem.play('powerup_get');
          this.powerups.splice(i, 1);
          break;
        }
      }
    }
  }

  private checkExitCollision(): void {
    if (!this.exitActive) return;

    for (const player of this.players) {
      if (!player.active || player.isDead || player.isDying) continue;
      const pp = player.getGridPos();
      if (pp.col === this.exitCol && pp.row === this.exitRow) {
        this.onLevelComplete();
        return;
      }
    }
  }

  // ── Game events ─────────────────────────────────────────────────────────────

  private onPlayerDied(player: Player): void {
    if (this.transitionActive) return;
    events.emit('player:died', { playerId: player.playerId });
    audioSystem.play('player_die');
    this.hud.flash(0xff2200, 0.5);

    if (this.testStage && player.stats.lives <= 0) player.stats.lives = 3;
    if (player.stats.lives <= 0) {
      if (this.players.every(p => p.isDead)) {
        this.time.delayedCall(RESPAWN_DELAY_MS, () => this.onGameOver());
      }
      return;
    }

    // Respawn after delay
    this.time.delayedCall(RESPAWN_DELAY_MS, () => {
      if (!this.transitionActive) {
        const start = this.generated.playerStarts[player.playerId] ?? this.generated.playerStarts[0];
        player.respawn(start.col, start.row);
        events.emit('player:respawned', { playerId: player.playerId });
      }
    });
  }

  private onEnemyKilled(enemy: Enemy, index: number): void {
    events.emit('enemy:killed', { type: enemy.type, col: enemy.col, row: enemy.row });
    audioSystem.play('enemy_die');
    enemy.die();
    this.enemies.splice(index, 1);
    if (!this.testStage) saveSystem.incrementStat('totalKills');
  }

  private checkLevelComplete(): void {
    if (this.testStage) return;
    if (this.currentMode === GameMode.SURVIVAL) {
      if (!this.nextWavePending && !this.gameOverFlag && !this.transitionActive && this.enemies.every(enemy => !enemy.alive || !enemy.active)) {
        this.nextWavePending = true;
        this.time.delayedCall(1400, () => { if (!this.gameOverFlag && !this.transitionActive) this.startSurvivalWave(); });
      }
      return;
    }
    if (this.levelComplete || this.gameOverFlag) return;
    if (this.transitionActive) return;

    const allDead = this.enemies.every(e => !e.alive || !e.active);
    if (allDead && !this.exitActive) {
      // Reveal exit if not already done
      if (this.exitSprite) {
        this.activateExit();
      } else {
        // No blocks left hiding exit
        this.revealExit();
        this.activateExit();
      }
    }
  }

  private onLevelComplete(): void {
    if (this.transitionActive) return;
    this.transitionActive = true;
    this.levelComplete = true;

    for (const player of this.players) player.celebrate();
    const timeBonus = Math.floor(this.timeLeft) * 10;
    this.scoreSystem.addTimeBonus(Math.floor(this.timeLeft));

    audioSystem.play('level_complete');
    audioSystem.stopMusic();

    if (this.currentMode !== GameMode.ARCADE) saveSystem.unlockLevel(this.currentLevel + 1);
    saveSystem.updateHighScore(this.scoreSystem.score);

    events.emit('level:complete', { score: this.scoreSystem.score, timeBonus });

    this.time.delayedCall(LEVEL_COMPLETE_DELAY_MS, () => {
      this.scene.start('VictoryScene', {
        ...this.getRunSettings(),
        score: this.scoreSystem.score,
        level: this.currentLevel,
        timeBonus,
        nextLevel: this.currentMode === GameMode.ARCADE ? 1 : this.currentLevel + 1,
        mode: this.currentMode,
      });
    });
  }

  private onGameOver(): void {
    if (this.transitionActive) return;
    this.transitionActive = true;
    this.gameOverFlag = true;

    audioSystem.play('game_over');
    audioSystem.stopMusic();

    saveSystem.updateHighScore(this.scoreSystem.score);
    events.emit('game:over', { score: this.scoreSystem.score, level: this.currentLevel });

    this.time.delayedCall(1000, () => {
      this.scene.start('GameOverScene', {
        ...this.getRunSettings(),
        score: this.scoreSystem.score,
        level: this.currentLevel,
        mode: this.currentMode,
      });
    });
  }

  private onTimeUp(): void {
    if (this.transitionActive) return;
    if (this.currentMode === GameMode.ARCADE) { this.onGameOver(); return; }
    // Kill all enemies and give player a chance to exit, or game over
    for (const e of this.enemies) {
      if (e.alive) e.takeDamage(99);
    }
    this.enemies = [];
    this.revealExit();
    this.activateExit();
  }

  // ── Pause ────────────────────────────────────────────────────────────────────

  private showPause(): void {
    if (this.transitionActive || this.paused) return;
    this.paused = true;
    this.scene.pause();
    this.events.once(Phaser.Scenes.Events.RESUME, () => this.paused = false);
    this.scene.launch('PauseScene', { level: this.currentLevel, mode: this.currentMode, ...this.getRunSettings() });
  }

  private startSurvivalWave(): void {
    if (this.gameOverFlag || this.transitionActive) return;
    this.survivalWave++;
    const available: { col:number; row:number }[] = [];
    for (let row=1; row<MAP_ROWS-1; row++) for(let col=1; col<MAP_COLS-1; col++) {
      if (this.grid[row][col] !== CellType.FLOOR || this.bombSystem.getBombAt(col,row)) continue;
      if (this.players.some(player => Math.hypot(player.x-(col*TILE+TILE/2),player.y-(row*TILE+TILE/2)) < TILE*4)) continue;
      available.push({col,row});
    }
    Phaser.Utils.Array.Shuffle(available);
    const types = this.survivalWave >= 3 ? [EnemyType.FAST, EnemyType.HUNTER, EnemyType.SMART, EnemyType.BOMBER] : [EnemyType.BASIC, EnemyType.FAST, EnemyType.HUNTER];
    const count=Math.min(12,3+this.survivalWave,available.length);
    this.enemies = [];
    for(let i=0;i<count;i++) {
      const position=available[i]; const enemy=new Enemy(this,position.col,position.row,types[i%types.length]).setDepth(4);
      enemy.speed *= 1.2 + Math.min(0.8,(this.survivalWave-1)*0.08);
      enemy.setBombCollisionQuery((col,row)=>this.bombSystem.getBombAt(col,row)?.state===BombState.ARMED);
      this.enemies.push(enemy);
    }
    if (!count) {
      // Wait for a safe floor cell rather than spawning an enemy on the player.
      this.survivalWave--; this.time.delayedCall(1000,()=>this.startSurvivalWave()); return;
    }
    this.waveBanner?.setText(`SUPRAVIEȚUIRE • VAL ${this.survivalWave}`);
    for(const type of [PowerUpType.EXTRA_LIFE,PowerUpType.SPEED_UP]) {
      const position=available.pop();
      if(position && !this.enemies.some(e=>e.col===position.col&&e.row===position.row) && !this.powerups.some(p=>p.col===position.col&&p.row===position.row))
        this.powerups.push(new PowerUp(this,position.col,position.row,type).setDepth(2));
    }
    this.nextWavePending=false;
  }

  getRunSettings(): RunSettings {
    return { playerCount: this.playerCount, playerNames: [...this.playerNames], testStage: this.testStage };
  }

  private setupRoster(): void {
    this.roster = this.players.map((player, i) => this.add.text(i * MAP_W / this.playerCount + 12, 8,
      `${this.playerNames[i]} • ${player.stats.lives} vieți`, { fontFamily: 'Arial', fontSize: '14px',
        color: '#' + PLAYER_COLORS[i].toString(16).padStart(6, '0'), backgroundColor: '#102033',
        padding: { x: 5, y: 4 } }).setDepth(30));
  }

  private refillTestSurprises(): void {
    for (const bonus of this.powerups) if (bonus.active) bonus.destroy();
    this.powerups = [];
    for (const item of this.generated.powerups) this.powerups.push(new PowerUp(this, item.col, item.row, item.type).setDepth(2));
  }

  private setupTestStage(): void {
    for (const item of this.generated.powerups) {
      const name = POWERUP_LEGEND.find(entry => entry.type === item.type)?.name.split(' • ')[0] ?? item.type;
      this.add.text(item.col*TILE+TILE/2, item.row*TILE+TILE-5, name,
        { fontFamily: 'Arial', fontSize: '12px', color: '#ffffff', backgroundColor: '#102033' }).setOrigin(0.5).setDepth(3);
    }
    this.doPlaceBomb(-1, 5, 8, 1, false, true);
    this.add.text(5*TILE+TILE/2, 9*TILE, 'Bombă pentru împins', { fontSize: '12px', color: '#ffffff', backgroundColor: '#102033' }).setOrigin(0.5).setDepth(3);
    this.labStatus = createLabPanel(this, {
      reset: () => this.scene.restart({ level: 1, mode: GameMode.CAMPAIGN, score: 0, testStage: true }),
      refill: () => this.refillTestSurprises(),
      dog: () => {
        if (this.enemies.length) { for (const enemy of this.enemies) enemy.destroy(); this.enemies = []; }
        else {
          const enemy = new Enemy(this, 12, 10, EnemyType.HUNTER).setDepth(4);
          enemy.setBombCollisionQuery((col, row) => this.bombSystem.getBombAt(col, row)?.state === BombState.ARMED);
          this.enemies.push(enemy);
        }
      },
      back: () => this.scene.start('MainMenuScene'),
    });
  }

  // ── Bind events ──────────────────────────────────────────────────────────────

  private bindEvents(): void {
    events.on('score:changed', ({ score }) => {
      if (!this.testStage) saveSystem.updateHighScore(score);
    });
  }

  // ── Snow ─────────────────────────────────────────────────────────────────────

  private updateSnow(delta: number): void {
    const dt = delta / 1000;
    const g = this.snowGfx;
    g.clear();

    for (const flake of this.snowflakes) {
      flake.phase += dt * 1.2;
      flake.y += flake.speed * dt;
      flake.x += Math.sin(flake.phase) * flake.drift * dt;

      // Wrap around
      if (flake.y > MAP_H) {
        flake.y = -4;
        flake.x = Math.random() * GAME_W;
      }
      if (flake.x < -4)      flake.x += GAME_W;
      if (flake.x > GAME_W)  flake.x -= GAME_W;

      const alpha = 0.4 + Math.sin(flake.phase * 0.5) * 0.2;
      g.fillStyle(0xddeeff, alpha);
      g.fillCircle(flake.x, flake.y, flake.size);
    }
  }

  // ── Shutdown ─────────────────────────────────────────────────────────────────

  shutdown(): void {
    this.events.off('mobile:pause', this.showPause, this);
    events.removeAll();
    this.bombSystem.clear();
    this.bombs.clear();
    this.blockMap.clear();
    this.exitSprite = null;
    this.players = [];
    this.enemies = [];
    this.powerups = [];
    this.activeExplosions = [];
  }
}

export default GameScene;
